import { callBackNumber, callOutcomes, type CallQuery, type CallSummary, type Me, type RegionSummary, type UserSummary } from '@smarton-pbx/shared';
import { ArrowDownLeft, ArrowLeftRight, ArrowUpRight, AudioLines, FileText, History, Phone, Search } from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../api';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { EmptyState, Loading, Notice, PageHeader, Pill } from '../components/ui';
import { locale, message, rich, t, tc } from '../i18n';
import { formatNumber, outcome, refusalText } from '../lib/calls';
import { useLoad } from '../lib/hooks';
import { usePhone } from './softphone';

export const directions = {
  INBOUND: { label: t('Incoming'), icon: ArrowDownLeft, color: 'var(--success)' },
  OUTBOUND: { label: t('Outgoing'), icon: ArrowUpRight, color: 'var(--brand-ink)' },
  INTERNAL: { label: t('Internal'), icon: ArrowLeftRight, color: 'var(--text-3)' },
} as const;

export function duration(seconds: number | null): string {
  if (seconds === null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = String(seconds % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
}

export function when(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const time = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  if (date.toDateString() === today.toDateString()) return t('Today, {time}', { time });
  return `${date.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}, ${time}`;
}

/** Text with search matches marked [[like this]], shown with highlights (never as HTML). */
export function Highlighted({ text }: { text: string }): ReactNode {
  return text.split(/(\[\[.*?\]\])/).map((part, i) =>
    part.startsWith('[[') && part.endsWith(']]') ? <mark key={i}>{part.slice(2, -2)}</mark> : <Fragment key={i}>{part}</Fragment>,
  );
}

const outcomeNames: Record<(typeof callOutcomes)[number], string> = {
  ANSWERED: t('Answered'),
  MISSED: t('Missed'),
  VOICEMAIL: t('Voicemail'),
  NO_ANSWER: t('No answer'),
  BUSY: t('Busy'),
  FAILED: t('Failed'),
  REJECTED: t('Not allowed'),
  CANCELLED: t('Cancelled'),
};

/** The other party, as people think of them. */
/**
 * A number on a call: the customer's shows their contact's name, linked to
 * their card; ours, the colleague who made or took the call.
 */
function Party({ call, number, theirs }: { call: CallSummary; number: string; theirs: boolean }) {
  const name = theirs ? call.contactName : call.userName;
  if (!name) return <>{formatNumber(number)}</>;
  return (
    <span>
      {theirs ? (
        <Link to={`/contacts/${call.contactId}`} onClick={(e) => e.stopPropagation()} className="plain-link" data-testid="call-contact">
          {name}
        </Link>
      ) : (
        <span data-testid="call-staff">{name}</span>
      )}
      <span className="subtle" style={{ display: 'block', fontSize: '0.8rem' }}>
        {formatNumber(number)}
      </span>
    </span>
  );
}

export function otherParty(call: CallSummary): string {
  if (call.direction === 'INTERNAL') return call.toUserName ?? call.toNumber;
  return call.contactName ?? formatNumber(call.direction === 'INBOUND' ? call.fromNumber : call.toNumber);
}

/** Calls the other party back from the browser phone (see callBackNumber). */
export function CallBackButton({ call, me, label = false }: { call: CallSummary; me: Me; label?: boolean }) {
  const phone = usePhone();
  const number = callBackNumber(call, me.id);
  if (!number) return null;
  const who = call.direction === 'INTERNAL' ? (call.userId === me.id ? (call.toUserName ?? number) : (call.userName ?? number)) : (call.contactName ?? formatNumber(number));
  return (
    <button
      type="button"
      className={label ? 'secondary small' : 'secondary small icon-button'}
      disabled={phone.status !== 'registered' || phone.busy}
      onClick={(e) => {
        e.stopPropagation();
        phone.dial(number);
      }}
      title={phone.status !== 'registered' ? t('The phone in this browser isn’t connected') : t('Call {name}', { name: who })}
      aria-label={t('Call {name}', { name: who })}
      data-testid={label ? 'call-page-call' : 'call-back'}
    >
      <Phone size={14} aria-hidden />
      {label && ` ${t('Call')}`}
    </button>
  );
}

const FILTERS = ['q', 'direction', 'outcome', 'userId', 'regionId', 'from', 'to', 'reasonCategoryId', 'saleOutcome'] as const;

/** One side of a call in words, for Excel: the colleague or contact, and the number. */
function sideText(call: CallSummary, side: 'from' | 'to'): string {
  const number = side === 'from' ? call.fromNumber : call.toNumber;
  if (call.direction === 'INTERNAL') return (side === 'from' ? call.userName : call.toUserName) ?? number;
  const theirs = side === 'from' ? call.direction === 'INBOUND' : call.direction === 'OUTBOUND';
  const name = theirs ? call.contactName : call.userName;
  return name ? `${name} (${number})` : number;
}

function callColumns(me: Me): Column<CallSummary>[] {
  const columns: Column<CallSummary>[] = [
    {
      key: 'call',
      label: tc('column', 'Call'),
      fixed: true,
      cell: (call) => {
        const direction = directions[call.direction];
        const Icon = direction.icon;
        return (
          <span className="row" style={{ gap: '0.6rem', flexWrap: 'nowrap' }}>
            <span className="avatar square" style={{ width: 30, height: 30, color: direction.color, background: 'var(--surface-2)' }}>
              <Icon size={16} aria-hidden />
            </span>
            <span className="hide-mobile">
              <Link to={`/calls/${call.id}`} onClick={(e) => e.stopPropagation()} className="plain-link">
                {direction.label}
              </Link>
            </span>
          </span>
        );
      },
      value: (call) => directions[call.direction].label,
    },
    {
      key: 'from',
      label: t('From'),
      className: 'hide-mobile tabular',
      cell: (call) =>
        call.direction === 'INTERNAL' ? (call.userName ?? call.fromNumber) : <Party call={call} number={call.fromNumber} theirs={call.direction === 'INBOUND'} />,
      value: (call) => sideText(call, 'from'),
    },
    {
      key: 'to',
      label: tc('column', 'To'),
      className: 'hide-mobile tabular',
      cell: (call) =>
        call.direction === 'INTERNAL' ? (call.toUserName ?? call.toNumber) : <Party call={call} number={call.toNumber} theirs={call.direction === 'OUTBOUND'} />,
      value: (call) => sideText(call, 'to'),
    },
    {
      key: 'person',
      label: t('Person'),
      hidden: true,
      className: 'hide-mobile',
      cell: (call) => call.userName ?? '—',
      value: (call) => call.userName,
    },
    {
      // Narrow screens only, in place of From and To: not in the ⚙ list nor in Excel.
      key: 'number',
      label: t('Number'),
      fixed: true,
      className: 'show-mobile',
      cell: (call) => (
        <>
          <div className="tabular">{otherParty(call)}</div>
          <div className="subtle" style={{ fontSize: '0.8rem' }}>
            {when(call.startedAt)}
            {call.userName && ` · ${call.userName}`}
          </div>
        </>
      ),
    },
    {
      key: 'outcome',
      label: t('Outcome'),
      cell: (call) => {
        const result = outcome(call);
        return (
          <span data-testid="call-outcome" data-outcome={call.outcome ?? ''} title={call.outcome === 'REJECTED' ? message(refusalText(call.rejectReason)) : undefined}>
            <Pill {...(result.kind && { kind: result.kind })}>{result.label}</Pill>
          </span>
        );
      },
      value: (call) => (call.outcome === 'REJECTED' ? `${outcome(call).label}: ${message(refusalText(call.rejectReason))}` : outcome(call).label),
    },
    { key: 'when', label: t('When'), className: 'hide-mobile muted', cell: (call) => when(call.startedAt), value: (call) => new Date(call.startedAt) },
    {
      key: 'duration',
      label: t('Duration'),
      className: 'hide-mobile tabular',
      cell: (call) => duration(call.durationSeconds),
      // Seconds, for sums in Excel.
      excel: [{ label: `${t('Duration')} (s)`, value: (call) => call.durationSeconds }],
    },
    {
      key: 'media',
      label: t('Recording and transcript'),
      quietHeading: true,
      className: 'hide-mobile media-icons',
      cell: (call) => (
        <>
          {call.recording === 'READY' && <AudioLines size={16} aria-label={t('Recording')} />}
          {call.transcript === 'DONE' && <FileText size={16} aria-label={t('Transcript')} />}
        </>
      ),
      excel: [
        { label: t('Recording'), value: (call) => (call.recording === 'READY' ? t('Yes') : null) },
        { label: t('Transcript'), value: (call) => (call.transcript === 'DONE' ? t('Yes') : null) },
      ],
    },
  ];
  // An agent's calls are all their own.
  return me.role === 'AGENT' ? columns.filter((column) => column.key !== 'person') : columns;
}

/** Every call the filters match, page by page, for a download: up to 10 000. */
async function allCalls(query: CallQuery): Promise<CallSummary[]> {
  const rows: CallSummary[] = [];
  let before: string | null = null;
  do {
    const page = await api.listCalls({ ...query, limit: 200, ...(before && { before }) });
    rows.push(...page.items);
    before = page.nextCursor;
  } while (before && rows.length < 10_000);
  return rows;
}

export function Calls({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => Object.fromEntries(FILTERS.map((key) => [key, params.get(key) ?? ''])) as Record<(typeof FILTERS)[number], string>, [params]);
  const [search, setSearch] = useState(filters.q);
  const [items, setItems] = useState<CallSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [loadingMore, setLoadingMore] = useState(false);
  const navigate = useNavigate();
  const canFilterPeople = me.role !== 'AGENT';
  const people = useLoad(() => (canFilterPeople ? api.listUsers() : Promise.resolve([] as UserSummary[])), [canFilterPeople]);
  const regions = useLoad(() => (me.role === 'TENANT_ADMIN' ? api.listRegions() : Promise.resolve([] as RegionSummary[])), [me.role]);
  // Opened from Insights: the calls behind a reason.
  const reasonName = useLoad(
    () => (filters.reasonCategoryId && me.role !== 'AGENT' ? api.listCategories() : Promise.resolve([])).then((list) => list.find((c) => c.id === filters.reasonCategoryId)?.name ?? null),
    [filters.reasonCategoryId],
  );

  const query = useMemo((): CallQuery => {
    const q: Record<string, string> = {};
    for (const key of FILTERS) if (filters[key]) q[key] = filters[key];
    return q as CallQuery;
  }, [filters]);

  useEffect(() => {
    let current = true;
    setItems(null);
    setError(undefined);
    api.listCalls(query).then(
      (page) => {
        if (!current) return;
        setItems(page.items);
        setCursor(page.nextCursor);
      },
      (err: unknown) => current && setError(err instanceof Error ? err.message : String(err)),
    );
    return () => {
      current = false;
    };
  }, [query]);

  // Search as the person types, after a short pause.
  useEffect(() => {
    if (search === filters.q) return;
    const timer = setTimeout(() => setFilter('q', search.trim()), 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function setFilter(key: (typeof FILTERS)[number], value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const page = await api.listCalls({ ...query, before: cursor });
      setItems((list) => [...(list ?? []), ...page.items]);
      setCursor(page.nextCursor);
    } finally {
      setLoadingMore(false);
    }
  }

  const filtered = FILTERS.some((key) => filters[key]);
  const table = useTable('calls', callColumns(me), mayDownload(me.role) ? { name: t('Calls'), rows: () => allCalls(query) } : null);

  return (
    <div className="stack">
      <PageHeader
        title={t('Calls')}
        description={me.role === 'TENANT_ADMIN' ? t('Every call, with recordings and transcripts.') : t('Calls in your region, with recordings and transcripts.')}
      />
      <div className="card filters">
        <label className="input-icon grow" style={{ minWidth: 'min(100%, 18rem)' }}>
          <Search size={16} aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('A number, or words said in the call')}
            aria-label={t('Search calls')}
            data-testid="call-search"
          />
        </label>
        <select value={filters.direction} onChange={(e) => setFilter('direction', e.target.value)} aria-label={t('Direction')}>
          <option value="">{t('All directions')}</option>
          <option value="INBOUND">{t('Incoming')}</option>
          <option value="OUTBOUND">{t('Outgoing')}</option>
          <option value="INTERNAL">{t('Internal')}</option>
        </select>
        <select value={filters.outcome} onChange={(e) => setFilter('outcome', e.target.value)} aria-label={t('Outcome')}>
          <option value="">{t('Any outcome')}</option>
          {callOutcomes.map((o) => (
            <option key={o} value={o}>
              {outcomeNames[o]}
            </option>
          ))}
        </select>
        {canFilterPeople && (
          <select value={filters.userId} onChange={(e) => setFilter('userId', e.target.value)} aria-label={t('Person')} data-testid="call-person">
            <option value="">{t('Everyone')}</option>
            {people.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        )}
        {me.role === 'TENANT_ADMIN' && (regions.data?.length ?? 0) > 1 && (
          <select value={filters.regionId} onChange={(e) => setFilter('regionId', e.target.value)} aria-label={t('Region')}>
            <option value="">{t('All regions')}</option>
            {regions.data?.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        )}
        <span className="row date-range">
          <input type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => setFilter('from', e.target.value)} aria-label={t('From')} />
          <span className="subtle">–</span>
          <input type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => setFilter('to', e.target.value)} aria-label={t('To')} />
        </span>
        {filters.reasonCategoryId && (
          <span className="chip" data-testid="reason-filter">
            {t('Reason: {name}', { name: reasonName.data ?? t('chosen reason') })}
            <button type="button" onClick={() => setFilter('reasonCategoryId', '')} aria-label={t('Remove the reason filter')}>
              ×
            </button>
          </span>
        )}
        {filtered && (
          <button
            type="button"
            className="ghost small"
            onClick={() => {
              setSearch('');
              setParams(new URLSearchParams(), { replace: true });
            }}
          >
            {t('Clear')}
          </button>
        )}
      </div>
      {error && <Notice kind="error">{error}</Notice>}
      {!items ? (
        <Loading />
      ) : items.length === 0 ? (
        <div className="card">
          <EmptyState icon={History} title={filtered ? t('No calls match') : t('No calls yet')}>
            <p>{filtered ? t('Try other filters or words.') : t('Calls appear here as they happen.')}</p>
          </EmptyState>
        </div>
      ) : (
        <div className="card table-wrap">
          <table className="calls-table">
            <TableHead table={table} />
            <tbody>
              {items.map((call) => {
                const open = () => void navigate(`/calls/${call.id}${filters.q ? `?q=${encodeURIComponent(filters.q)}` : ''}`);
                return (
                  <Fragment key={call.id}>
                    <tr className="clickable" onClick={open} data-testid="call-row" data-call-id={call.id}>
                      <Cells table={table} row={call} />
                      <td className="call-back-cell">
                        <CallBackButton call={call} me={me} />
                      </td>
                    </tr>
                    {call.snippet && (
                      <tr className="snippet-row clickable" onClick={open}>
                        <td colSpan={table.columns.length + 1}>
                          <span className="snippet" data-testid="call-snippet">
                            {rich('“{text}”', { text: <Highlighted text={call.snippet} /> })}
                          </span>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {cursor && (
            <div className="card-footer" style={{ justifyContent: 'center' }}>
              <button type="button" className="secondary" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? t('Loading…') : t('Show more')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
